var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { TextField as MuiTextField, FormControl as MuiFormControl, FormHelperText as MuiFormHelperText, Input as MuiInput, InputAdornment as MuiInputAdornment, } from '@material-ui/core';
import { CheckCircle as CheckCircleIcon, WarningRounded as WarningRoundedIcon, Error as ErrorIcon } from '@material-ui/icons';
import clsx from 'clsx';
import Select from '../Select/Select';
var useStyles = makeStyles(function (theme) { return ({
    root: {
        margin: theme.spacing(1),
    },
    fullWidth: {
        width: '100%',
    },
    formControl: function (props) { return ({
        width: '100%',
        borderRadius: 0,
        border: "1px solid ".concat(props.error ? theme.palette.error.main : theme.palette.grey[400]),
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(0.5),
        '&:hover': {
            border: "1px solid ".concat(props.error ? theme.palette.error.dark : theme.palette.grey[600]),
            zIndex: 1,
        },
    }); },
    label: function (props) { return ({
        fontSize: '.8em',
        color: props.error ? theme.palette.error.main : theme.palette.text.secondary,
    }); },
    labelWrap: function () { return ({
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
    }); },
    input: {
        fontSize: '.8rem',
        marginTop: '0 !important',
        '&>input': {
            padding: 0,
        },
        '&>div>div': {
            paddingTop: '0',
            paddingBottom: '0',
        },
        '&>div>select': {
            paddingTop: '0',
            paddingBottom: '0',
        },
    },
    inputAutofill: {
        '&:-webkit-autofill': {
            WebkitBoxShadow: "0 0 0 1000px ".concat(theme.palette.background.default, " inset"),
            WebkitTextFillColor: theme.palette.text.primary
        }
    },
    multiline: {
        padding: '0 !important',
    },
    helperText: {
        color: theme.palette.text.hint,
        marginTop: theme.spacing(0.25),
        marginLeft: theme.spacing(0.5),
        marginBottom: 0,
        marginRight: theme.spacing(0.5),
    },
    readOnly: {
        color: theme.palette.text.primary,
    },
    success: function (props) { return ({
        color: theme.palette.success.main,
        opacity: (props.disabled || props.readOnly) && '50%',
    }); },
    warning: function (props) { return ({
        color: theme.palette.warning.main,
        opacity: (props.disabled || props.readOnly) && '50%',
    }); },
    error: function (props) { return ({
        color: theme.palette.error.main,
        opacity: (props.disabled || props.readOnly) && '50%',
    }); },
    notchedOutline: {
        borderColor: 'transparent !important',
    },
    defaultAdornment: {
        position: 'relative',
        top: '-.5rem',
    },
}); });
/**
## EndAdornments and StatusIcons
The **TextField** supports the standard `endAdornment` property that is passed in the `InputProps`. **TextFields** also have the ability to display a `statusIcon` which is displayed as an end adorment. If both are present, the `statusIcon` will override the `endAdornment`.
 */
export default function TextField(props) {
    var _a = props.variant, variant = _a === void 0 ? 'standard' : _a, label = props.label, helperText = props.helperText, _b = props.required, required = _b === void 0 ? false : _b, _c = props.disabled, disabled = _c === void 0 ? false : _c, _d = props.fullWidth, fullWidth = _d === void 0 ? true : _d, margin = props.margin, inputProps = props.inputProps, InputProps = props.InputProps, _e = props.statusIcon, statusIcon = _e === void 0 ? 'none' : _e, select = props.select, SelectProps = props.SelectProps, className = props.className, _f = props.maxLength, maxLength = _f === void 0 ? -1 : _f, shrinkLabel = props.shrinkLabel, otherProps = __rest(props, ["variant", "label", "helperText", "required", "disabled", "fullWidth", "margin", "inputProps", "InputProps", "statusIcon", "select", "SelectProps", "className", "maxLength", "shrinkLabel"]);
    var readOnly = (InputProps || { readOnly: props.readOnly || false }).readOnly;
    var classes = useStyles(props);
    var _g = React.useState(false), focused = _g[0], setFocused = _g[1];
    var onFocus = function () {
        setFocused(true);
    };
    var onBlur = function () {
        setFocused(false);
    };
    var getStatusIcon = function (statusIcon) {
        switch (statusIcon) {
            case 'success': return React.createElement(CheckCircleIcon, { className: classes.success });
            case 'warning': return React.createElement(WarningRoundedIcon, { className: classes.warning });
            case 'error': return React.createElement(ErrorIcon, { className: classes.error });
            default: return undefined;
        }
    };
    var icon = getStatusIcon(statusIcon);
    if (select) {
        var 
        // eslint-disable-next-line no-shadow
        SelectProps_1 = props.SelectProps, select_1 = props.select, statusIcon_1 = props.statusIcon, margin_1 = props.margin, helperText_1 = props.helperText, shrinkLabel_1 = props.shrinkLabel, onChange = props.onChange, onKeyUp = props.onKeyUp, onKeyDown = props.onKeyDown, otherProps_1 = __rest(props, ["SelectProps", "select", "statusIcon", "margin", "helperText", "shrinkLabel", "onChange", "onKeyUp", "onKeyDown"]);
        return (React.createElement(Select, __assign({}, SelectProps_1, otherProps_1, { helperText: helperText_1 || undefined, onChange: onChange, onKeyUp: onKeyUp, onKeyDown: onKeyDown, disabled: disabled || readOnly, readOnly: readOnly })));
    }
    if (variant !== 'default') {
        // This is a patch for a MUI bug
        // See: https://github.com/mui-org/material-ui/issues/15697
        var variantFixedProps = (function () {
            var tsVariant;
            switch (variant) {
                case 'outlined': {
                    tsVariant = { variant: variant };
                    break;
                }
                case 'filled': {
                    tsVariant = { variant: 'filled' };
                    break;
                }
                case 'standard':
                default: {
                    tsVariant = { variant: 'standard' };
                    break;
                }
            }
            var p = __assign({}, props);
            delete p.variant;
            return __assign(__assign({}, p), tsVariant);
        })();
        var 
        // eslint-disable-next-line no-shadow
        _h = variantFixedProps.statusIcon, 
        // eslint-disable-next-line no-shadow
        statusIcon_2 = _h === void 0 ? 'none' : _h, inputProps_1 = variantFixedProps.inputProps, InputProps_1 = variantFixedProps.InputProps, maxLength_1 = variantFixedProps.maxLength, shrinkLabel_2 = variantFixedProps.shrinkLabel, otherFixedProps = __rest(variantFixedProps, ["statusIcon", "inputProps", "InputProps", "maxLength", "shrinkLabel"]);
        return (React.createElement(MuiTextField, __assign({}, otherFixedProps, { inputProps: __assign({ className: classes.inputAutofill, maxLength: maxLength_1 }, inputProps_1), disabled: disabled || readOnly, InputLabelProps: { classes: { root: clsx(classes.labelWrap) }, shrink: shrinkLabel_2 }, 
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps: __assign(__assign({}, InputProps_1), { classes: { disabled: clsx(readOnly && classes.readOnly) }, readOnly: readOnly, endAdornment: (statusIcon_2 && statusIcon_2 !== 'none')
                    ? React.createElement(MuiInputAdornment, { position: "end" },
                        React.createElement(React.Fragment, null, icon))
                    : (InputProps_1 && InputProps_1.endAdornment) }) })));
    }
    return (React.createElement("div", { className: clsx(classes.root, className, fullWidth && classes.fullWidth) },
        React.createElement(MuiFormControl, { disabled: disabled || readOnly, className: clsx(classes.formControl) },
            label && (React.createElement("label", { htmlFor: props.id, className: classes.label },
                label,
                required && ' *')),
            React.createElement(MuiInput, __assign({}, otherProps, InputProps, { onKeyDown: props.onKeyDown, onKeyUp: props.onKeyUp, inputProps: __assign({ className: classes.inputAutofill, maxLength: maxLength }, inputProps), disableUnderline: true, disabled: disabled || readOnly, endAdornment: (statusIcon && statusIcon !== 'none')
                    ? React.createElement(MuiInputAdornment, { position: "end" },
                        React.createElement(React.Fragment, null, icon))
                    : (InputProps && InputProps.endAdornment), classes: {
                    root: clsx(classes.input),
                    multiline: clsx(classes.multiline),
                    disabled: clsx(readOnly && classes.readOnly),
                }, onFocus: onFocus, onBlur: onBlur }))),
        helperText && (React.createElement(MuiFormHelperText, { className: clsx(classes.helperText, props.error && classes.error) }, helperText))));
}
TextField.defaultProps = {
    fullWidth: true,
    maxLength: -1,
    required: false,
    readOnly: false,
    variant: 'standard',
    statusIcon: 'none',
};
//# sourceMappingURL=TextField.js.map